import { CollectionService } from '@affine/core/modules/collection';
import { DocsStore } from '@affine/core/modules/doc/stores/docs';
import { FolderStore } from '@affine/core/modules/organize';
import {
  useCollectionsFeatureEnabled,
  useTagsFeatureEnabled,
} from '@affine/core/modules/streamlain';
import { TagService } from '@affine/core/modules/tag';
import { useI18n } from '@affine/i18n';
import { LiveData, useLiveData, useService } from '@toeverything/infra';
import { useMemo } from 'react';

import {
  projectNavigationTree,
  type TreeNavigationDoc,
  type TreeNavigationRoot,
  type TreeNavigationRow,
} from './navigation';

/** Read the ordinary local/cloud metadata streams once; no per-node network or document scope. */
export function useNavigationTreeEntries(
  roots: readonly TreeNavigationRoot[],
  path: string[],
  hiddenDocIds?: ReadonlySet<string>
) {
  const folderStore = useService(FolderStore),
    docsStore = useService(DocsStore);
  const t = useI18n(),
    tagsEnabled = useTagsFeatureEnabled(),
    collectionsEnabled = useCollectionsFeatureEnabled();
  const tags = useLiveData(useService(TagService).tagList.tagMetas$);
  const collectionService = useService(CollectionService);
  const collections = useLiveData(
    useMemo(
      () =>
        LiveData.computed(get =>
          [...get(collectionService.collections$).values()].map(collection => ({
            id: collection.id,
            name: get(collection.name$),
          }))
        ),
      [collectionService]
    )
  );
  const rows = useLiveData(
    useMemo(
      () =>
        LiveData.from<TreeNavigationRow[]>(
          folderStore.watchNavigationRows(),
          []
        ),
      [folderStore]
    )
  );
  const docs = useLiveData(
    useMemo(
      () =>
        LiveData.from<TreeNavigationDoc[]>(
          docsStore.watchNavigationMetadata(),
          []
        ),
      [docsStore]
    )
  );
  return useMemo(() => {
    const tagNames = new Map(tags.map(tag => [tag.id, tag.name])),
      collectionNames = new Map(
        collections.map(collection => [collection.id, collection.name])
      );
    const allowed = (kind: string, id: string) =>
      kind === 'tag'
        ? tagsEnabled && tagNames.has(id)
        : kind === 'collection'
          ? collectionsEnabled && collectionNames.has(id)
          : true;
    return projectNavigationTree(
      roots.filter(root => allowed(root.kind, root.id)),
      rows.filter(row => allowed(row.type, row.data)),
      docs,
      path,
      hiddenDocIds
    ).map(entry => ({
      ...entry,
      label:
        entry.kind === 'tag'
          ? tagNames.get(entry.id)!
          : entry.kind === 'collection'
            ? collectionNames.get(entry.id)!
            : t.t(entry.label),
    }));
  }, [
    collections,
    collectionsEnabled,
    docs,
    hiddenDocIds,
    path,
    roots,
    rows,
    t,
    tags,
    tagsEnabled,
  ]);
}
